import React, { useEffect, useState } from 'react';
import { productAPI, orderAPI, addressAPI, categoryAPI, wishlistAPI } from '../../services/api';
import { getImgUrl } from '../../utils/config';
import ProductCard from '../../components/ProductCard';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { Link, useSearchParams } from 'react-router-dom';
import { ShoppingBag, Loader, Package, MapPin, X, Filter, ChevronDown, Truck, Heart, Search } from 'lucide-react';
import SEO from '../../components/SEO';

const Products = () => {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const { cart, addToCart: cartAddToCart, removeFromCart, isCartOpen, setIsCartOpen, clearCart } = useCart();
    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const { user, setShowAuthPopup } = useAuth();
    const [orderLoading, setOrderLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [sortBy, setSortBy] = useState('');

    const [shippingDetails, setShippingDetails] = useState({
        shippingAddress: '', city: '', zipCode: '', phone: '', village: '', district: '', state: ''
    });
    const [addresses, setAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState('');
    const [isNewAddress, setIsNewAddress] = useState(false);
    const [wishlistItems, setWishlistItems] = useState([]);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (user) {
            fetchWishlist();
        }
    }, [user]);

    useEffect(() => {
        const categoryId = searchParams.get('category');
        const search = searchParams.get('search');

        let filtered = [...products];

        if (categoryId) {
            setSelectedCategory(categoryId);
            filtered = filtered.filter(p => p.categoryId === parseInt(categoryId));
        }

        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(searchLower) ||
                p.description?.toLowerCase().includes(searchLower)
            );
        }

        if (selectedCategory && !categoryId) {
            filtered = filtered.filter(p => p.categoryId === parseInt(selectedCategory));
        }

        // Sort
        if (sortBy === 'price-low') {
            filtered.sort((a, b) => a.price - b.price);
        } else if (sortBy === 'price-high') {
            filtered.sort((a, b) => b.price - a.price);
        } else if (sortBy === 'name') {
            filtered.sort((a, b) => a.name.localeCompare(b.name));
        }

        setFilteredProducts(filtered);
    }, [products, searchParams, selectedCategory, sortBy]);

    const fetchData = async () => {
        try {
            const [productsRes, categoriesRes] = await Promise.all([
                productAPI.getAllUsers(),
                categoryAPI.getActive()
            ]);

            const transformedProducts = productsRes.data.products.map(p => {
                const v = p.variations?.[0] || {};
                let images = v.image;
                if (typeof images === 'string') {
                    try { images = JSON.parse(images); } catch (e) { images = []; }
                }
                return {
                    ...p,
                    price: v.price || 0,
                    originalPrice: v.originalPrice || (v.price ? v.price * 1.2 : 0),
                    image: images?.[0] || '',
                    images: images || [],
                    stock: v.stock ? 100 : 0,
                    variationId: v.id,
                    categoryId: v.categoryId
                };
            });
            setProducts(transformedProducts);
            setFilteredProducts(transformedProducts);
            setCategories(categoriesRes.data.data || []);
        } catch (error) {
            console.error("Failed to fetch data", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchWishlist = async () => {
        try {
            const response = await wishlistAPI.getAll();
            setWishlistItems(response.data?.items || []);
        } catch (error) {
            console.error('Failed to fetch wishlist', error);
        }
    };

    const fetchAddresses = async () => {
        try {
            const response = await addressAPI.getAll();
            if (response.data?.data) {
                setAddresses(response.data.data);
                if (response.data.data.length > 0) {
                    setSelectedAddressId(response.data.data[0].id);
                    setIsNewAddress(false);
                } else {
                    setIsNewAddress(true);
                }
            }
        } catch (error) {
            console.error("Failed to fetch addresses", error);
        }
    };

    const handleDeleteAddress = async (id) => {
        if (window.confirm("Delete this address?")) {
            try {
                await addressAPI.delete(id);
                fetchAddresses();
            } catch (error) {
                alert("Failed to delete address");
            }
        }
    };

    const addToCart = (product) => {
        if (!user) { setShowAuthPopup(true); return; }
        cartAddToCart(product);
        setIsCartOpen(true);
    };

    const handleRemoveFromCart = (id) => removeFromCart(id);
    const handleCheckoutClick = () => { setIsCartOpen(false); setIsCheckoutOpen(true); fetchAddresses(); };
    const handleBuyNow = (product) => {
        if (!user) { setShowAuthPopup(true); return; }
        addToCart(product);
        setIsCartOpen(false);
        setIsCheckoutOpen(true);
        fetchAddresses();
    };

    const placeOrder = async (e) => {
        e.preventDefault();
        setOrderLoading(true);
        try {
            let addressId = selectedAddressId;
            if (isNewAddress) {
                const addressData = {
                    name: user.name, mobile: shippingDetails.phone, address: shippingDetails.shippingAddress,
                    village: shippingDetails.village, city: shippingDetails.city, district: shippingDetails.district,
                    pincode: shippingDetails.zipCode, state: shippingDetails.state, country: 'India'
                };
                const addressResponse = await addressAPI.create(addressData);
                addressId = addressResponse.data.data.id;
            }
            if (!addressId) { alert("Please select or add an address."); setOrderLoading(false); return; }

            await orderAPI.placeOrder({
                address_id: addressId,
                items: cart.map(item => ({ product_variation_id: item.variationId, qty: item.quantity })),
                shippingCharges: 0, paymentMethod: "COD"
            });

            alert('Order placed successfully!');
            clearCart();
            setIsCheckoutOpen(false);
            setShippingDetails({ shippingAddress: '', city: '', zipCode: '', phone: '', village: '', district: '', state: '' });
            fetchData();
        } catch (error) {
            alert('Failed to place order: ' + (error.response?.data?.error || error.message));
        } finally {
            setOrderLoading(false);
        }
    };

    const cartTotal = cart.reduce((total, item) => total + (Number(item.price) * item.quantity), 0);


    const handleSearch = (e) => {
        e.preventDefault();
        const params = new URLSearchParams(searchParams);
        if (searchTerm.trim()) {
            params.set('search', searchTerm.trim());
        } else {
            params.delete('search');
        }
        setSearchParams(params);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <SEO
                title="Buy Agricultural Products Online - Seeds, Fertilizers & Tools"
                description="Shop high-quality agricultural products including seeds, fertilizers, pesticides, and farming tools. Best prices and fast delivery."
                keywords="Buy Seeds Online, Agricultural Products, Fertilizers, Pesticides, Farming Tools, Agri Shop"
            />
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-6">
                <div className="container mx-auto px-6">
                    <h1 className="text-2xl md:text-3xl font-bold">Our Products</h1>
                    <p className="text-green-100 mt-1">Quality agricultural products for your farm</p>
                </div>
            </div>

            <div className="container mx-auto px-4 py-6">
                {/* Filter Bar */}
                <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
                    {/* Search Bar */}
                    <form onSubmit={handleSearch} className="flex-1 w-full md:w-auto md:min-w-[300px] max-w-2xl relative order-last md:order-first">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search for products, seeds, pesticides..."
                            className="w-full px-4 py-2.5 pr-12 border-2 border-green-500 rounded-lg focus:outline-none focus:border-green-600 text-gray-700"
                        />
                        <button
                            type="submit"
                            className="absolute right-0 top-0 h-full px-4 bg-green-600 text-white rounded-r-lg hover:bg-green-700 transition"
                        >
                            <Search className="h-5 w-5" />
                        </button>
                    </form>

                    <div className="flex items-center gap-4 flex-wrap">
                        {/* Category Filter */}
                        <div className="relative">
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 pr-10 text-sm focus:outline-none focus:border-green-500"
                            >
                                <option value="">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>

                        {/* Sort */}
                        <div className="relative">
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 pr-10 text-sm focus:outline-none focus:border-green-500"
                            >
                                <option value="">Sort By</option>
                                <option value="price-low">Price: Low to High</option>
                                <option value="price-high">Price: High to Low</option>
                                <option value="name">Name</option>
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-500">{filteredProducts.length} Products</span>

                        {/* Cart Button */}
                        <button
                            onClick={() => setIsCartOpen(!isCartOpen)}
                            className="relative bg-green-600 text-white p-2.5 rounded-lg hover:bg-green-700 transition"
                        >
                            <ShoppingBag size={20} />
                            {cart.length > 0 && (
                                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                                    {cart.length}
                                </span>
                            )}
                        </button>

                        <Link to="/orders" className="bg-gray-100 p-2.5 rounded-lg hover:bg-gray-200 transition" title="Track Order">
                            <Truck size={20} className="text-gray-600" />
                        </Link>

                        <Link to="/wishlist" className="bg-gray-100 p-2.5 rounded-lg hover:bg-gray-200 transition" title="Wishlist">
                            <Heart size={20} className="text-gray-600" />
                        </Link>

                        <Link to="/my-addresses" className="bg-gray-100 p-2.5 rounded-lg hover:bg-gray-200 transition" title="My Addresses">
                            <MapPin size={20} className="text-gray-600" />
                        </Link>
                    </div>
                </div>

                {/* Products Grid */}
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent"></div>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-gray-500 text-lg">No products found</p>
                        <button onClick={() => { setSelectedCategory(''); setSortBy(''); }} className="mt-4 text-green-600 hover:underline">
                            Clear filters
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {filteredProducts.map(product => (
                            <ProductCard key={product.id} product={product} onAddToCart={addToCart} onBuyNow={handleBuyNow} wishlistItems={wishlistItems} />
                        ))}
                    </div>
                )}
            </div>

            {/* Shopping Cart Sidebar */}
            {isCartOpen && (
                <div className="fixed inset-0 z-50 overflow-hidden">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setIsCartOpen(false)}></div>
                    <div className="fixed inset-y-0 right-0 max-w-md w-full bg-white shadow-xl flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b">
                            <h2 className="text-lg font-bold text-gray-900">Shopping Cart ({cart.length})</h2>
                            <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4">
                            {cart.length === 0 ? (
                                <p className="text-center text-gray-500 mt-10">Your cart is empty</p>
                            ) : (
                                <div className="space-y-4">
                                    {cart.map((item) => (
                                        <div key={item.id} className="flex gap-4 p-3 bg-gray-50 rounded-lg">
                                            <img
                                                src={getImgUrl(item.image) || 'https://via.placeholder.com/80'}
                                                alt={item.name}
                                                className="w-20 h-20 object-cover rounded-lg"
                                            />
                                            <div className="flex-1">
                                                <h3 className="font-medium text-gray-800 text-sm line-clamp-2">{item.name}</h3>
                                                <p className="text-green-600 font-bold mt-1">₹{item.price} × {item.quantity}</p>
                                                <button onClick={() => removeFromCart(item.id)} className="text-red-500 text-xs mt-1 hover:underline">Remove</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="border-t p-4">
                            <div className="flex justify-between text-lg font-bold mb-4">
                                <span>Total</span>
                                <span className="text-green-600">₹{cartTotal.toFixed(2)}</span>
                            </div>
                            <button
                                onClick={handleCheckoutClick}
                                disabled={cart.length === 0}
                                className={`w-full py-3 rounded-lg font-bold transition ${cart.length > 0 ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-500'
                                    }`}
                            >
                                Proceed to Checkout
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Checkout Modal */}
            {isCheckoutOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen p-4">
                        <div className="absolute inset-0 bg-black/50" onClick={() => setIsCheckoutOpen(false)}></div>

                        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
                                <h3 className="text-lg font-bold">Checkout</h3>
                                <button onClick={() => setIsCheckoutOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={placeOrder} className="p-4">
                                {/* Address Selection */}
                                {addresses.length > 0 && (
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Address</label>
                                        <div className="space-y-2">
                                            {addresses.map((addr) => (
                                                <label key={addr.id} className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:border-green-500">
                                                    <input
                                                        type="radio"
                                                        checked={!isNewAddress && selectedAddressId === addr.id}
                                                        onChange={() => { setSelectedAddressId(addr.id); setIsNewAddress(false); }}
                                                        className="mt-1"
                                                    />
                                                    <div className="flex-1">
                                                        <p className="font-medium text-sm">{addr.type || 'Home'}</p>
                                                        <p className="text-gray-500 text-xs">{addr.address}, {addr.city}, {addr.state} - {addr.pincode}</p>
                                                    </div>
                                                    <button type="button" onClick={(e) => { e.preventDefault(); handleDeleteAddress(addr.id); }} className="text-red-500 text-xs">Delete</button>
                                                </label>
                                            ))}
                                            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:border-green-500">
                                                <input type="radio" checked={isNewAddress} onChange={() => setIsNewAddress(true)} />
                                                <span className="font-medium text-sm text-green-600">+ Add New Address</span>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {/* New Address Form */}
                                {(isNewAddress || addresses.length === 0) && (
                                    <div className="space-y-3 mb-4">
                                        <input type="tel" required placeholder="Phone Number" className="w-full border rounded-lg px-3 py-2 text-sm" value={shippingDetails.phone} onChange={(e) => setShippingDetails({ ...shippingDetails, phone: e.target.value })} />
                                        <textarea required placeholder="Full Address" rows="2" className="w-full border rounded-lg px-3 py-2 text-sm" value={shippingDetails.shippingAddress} onChange={(e) => setShippingDetails({ ...shippingDetails, shippingAddress: e.target.value })} />
                                        <div className="grid grid-cols-2 gap-3">
                                            <input type="text" required placeholder="City" className="border rounded-lg px-3 py-2 text-sm" value={shippingDetails.city} onChange={(e) => setShippingDetails({ ...shippingDetails, city: e.target.value })} />
                                            <input type="text" required placeholder="Pincode" className="border rounded-lg px-3 py-2 text-sm" value={shippingDetails.zipCode} onChange={(e) => setShippingDetails({ ...shippingDetails, zipCode: e.target.value })} />
                                        </div>
                                        <div className="grid grid-cols-3 gap-3">
                                            <input type="text" required placeholder="Village" className="border rounded-lg px-3 py-2 text-sm" value={shippingDetails.village} onChange={(e) => setShippingDetails({ ...shippingDetails, village: e.target.value })} />
                                            <input type="text" required placeholder="District" className="border rounded-lg px-3 py-2 text-sm" value={shippingDetails.district} onChange={(e) => setShippingDetails({ ...shippingDetails, district: e.target.value })} />
                                            <input type="text" required placeholder="State" className="border rounded-lg px-3 py-2 text-sm" value={shippingDetails.state} onChange={(e) => setShippingDetails({ ...shippingDetails, state: e.target.value })} />
                                        </div>
                                    </div>
                                )}

                                {/* Order Summary */}
                                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                    <div className="flex justify-between mb-2">
                                        <span className="text-gray-600">Subtotal</span>
                                        <span className="font-medium">₹{cartTotal.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between mb-2">
                                        <span className="text-gray-600">Shipping</span>
                                        <span className="text-green-600 font-medium">FREE</span>
                                    </div>
                                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                                        <span>Total</span>
                                        <span className="text-green-600">₹{cartTotal.toFixed(2)}</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">Payment: Cash on Delivery</p>
                                </div>

                                <div className="flex gap-3">
                                    <button type="button" onClick={() => setIsCheckoutOpen(false)} className="flex-1 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">
                                        Cancel
                                    </button>
                                    <button type="submit" disabled={orderLoading} className="flex-1 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 disabled:bg-gray-400">
                                        {orderLoading ? 'Placing...' : 'Place Order'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Products;
